/**
 * @file        src/components/RowMenu.jsx
 * @description Reusable per-row "kebab" actions menu — a small ⋮ button that
 *              opens a compact list of actions instead of a table row
 *              stacking 4-5 separate buttons. The popover is `position:fixed`,
 *              placed from the button's own bounding rect (computed on open,
 *              flipping upward near the viewport bottom and clamped to the
 *              right edge) — NOT `position:absolute` inside the row — because
 *              its home table lives in a bounded, `overflow:auto` scroll
 *              container (`.table-wrap.scroll`, the dense Booking List, CR
 *              D3) that would otherwise clip an absolutely-positioned dropdown
 *              near the bottom of the visible rows. Closes on outside click,
 *              Escape, or scroll (the fixed position would otherwise go stale
 *              relative to the button once the table scrolls under it).
 *              An item is either a click handler (`onClick`) or a route (`to`,
 *              rendered as a react-router <Link>); `hidden` drops an item
 *              entirely (e.g. Cancel once a booking is already cancelled).
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-08-04 Claude — Initial (Bookings list row-action density cleanup,
 *                          CR D3): replaces the flight/hotel BookingList's
 *                          `.row-actions` button row with one menu.
 *   - 2026-08-05 Claude — Portal the popover to document.body. Its `.panel
 *                          .glass` ancestor (ReadOnlyListPage) uses
 *                          `backdrop-filter`, which per spec creates a
 *                          containing block for `position:fixed` descendants —
 *                          same as `transform`. That silently turned the
 *                          popover's "fixed" positioning back into something
 *                          `.table-wrap.scroll`'s `overflow-y:auto` clips and
 *                          mispositions, so it opened cut off / in the wrong
 *                          spot and its lower items were unreachable (the
 *                          exact clipping bug the fixed positioning was
 *                          originally meant to dodge). Rendering via a portal
 *                          removes it from that ancestor chain entirely.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Icon from './Icon';

const POP_W = 180;
const POP_MAX_H = 220;

/**
 * @param {object} props
 * @param {Array<{key:string,label:string,icon?:string,onClick?:Function,to?:string,danger?:boolean,hidden?:boolean}>} props.items
 * @param {string} [props.label='Actions']
 */
export default function RowMenu({ items = [], label = 'Actions' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { left, top? , bottom? }
  const btnRef = useRef(null);
  const popRef = useRef(null);

  function openAt() {
    const r = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < POP_MAX_H && r.top > POP_MAX_H;
    const left = Math.max(8, Math.min(r.right - POP_W, window.innerWidth - POP_W - 8));
    setPos(openUp
      ? { left, bottom: window.innerHeight - r.top + 6 }
      : { left, top: r.bottom + 6 });
    setOpen((o) => !o);
  }

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!btnRef.current?.contains(e.target) && !popRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // capture:true so a scroll on ANY ancestor (e.g. the table's own
    // overflow:auto wrap) is caught, not just window-level scroll.
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const visible = items.filter((it) => !it.hidden);

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        className={`row-menu-btn${open ? ' open' : ''}`}
        onClick={openAt}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
      >
        <Icon name="more" size={16} />
      </button>
      {open && createPortal(
        <div className="row-menu-pop" role="menu" ref={popRef} style={{ left: pos.left, top: pos.top, bottom: pos.bottom }}>
          {visible.map((it) => (it.to ? (
            <Link
              key={it.key}
              to={it.to}
              role="menuitem"
              className={`row-menu-item${it.danger ? ' danger' : ''}`}
              onClick={() => setOpen(false)}
            >
              {it.icon ? <Icon name={it.icon} size={14} /> : null}
              <span>{it.label}</span>
            </Link>
          ) : (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              className={`row-menu-item${it.danger ? ' danger' : ''}`}
              onClick={() => { setOpen(false); it.onClick?.(); }}
            >
              {it.icon ? <Icon name={it.icon} size={14} /> : null}
              <span>{it.label}</span>
            </button>
          )))}
        </div>,
        document.body,
      )}
    </>
  );
}
